import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Eye, MessageSquare, Award, Star } from 'lucide-react';

const ProfessionalCard = ({ professional, onContact, showPortfolio = true }) => {
  const navigate = useNavigate();

  const getAvailabilityBadge = (availability) => {
    const badges = {
      available: { text: 'Available Now', className: 'bg-green-100 text-green-700 border-green-200' },
      within_week: { text: 'Within 1 Week', className: 'bg-blue-100 text-blue-700 border-blue-200' },
      within_month: { text: 'Within 1 Month', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' }
    };
    return badges[availability] || badges.available;
  };

  const availabilityBadge = getAvailabilityBadge(professional.availability);

  return (
    <Card className="bg-card border-border hover:border-accent/50 transition-all">
      <CardContent className="p-6">
        <div className="flex gap-6">
          <div className="flex-shrink-0">
            {showPortfolio && professional.portfolio && professional.portfolio.length > 0 ? (
              <div className="relative">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${professional.portfolio[0].image_url}`}
                  alt={`${professional.name} portfolio`}
                  className="w-24 h-24 rounded-lg object-cover border border-border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-24 h-24 rounded-lg bg-accent/10 hidden items-center justify-center text-3xl font-bold text-accent">
                  {professional.name.charAt(0)}
                </div>
                {professional.portfolio.length > 1 && (
                  <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    +{professional.portfolio.length - 1}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-24 h-24 rounded-lg bg-accent/10 flex items-center justify-center text-3xl font-bold text-accent">
                {professional.name.charAt(0)}
              </div>
            )}
          </div>

          <div className="flex-1">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-xl font-semibold">{professional.name}</h3>
                <p className="text-accent font-medium">{professional.designation}</p>
              </div>
              {professional.verified && (
                <Badge className="bg-accent/10 text-accent border-accent/20">
                  <Award className="w-3 h-3 mr-1" />Verified
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-3 text-sm text-muted-foreground">
              <span>📍 {professional.location?.city}</span>
              <span>•</span>
              <span>💼 {professional.experience} yrs</span>
              {professional.rating > 0 && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    {professional.rating.toFixed(1)}
                  </span>
                </>
              )}
            </div>

            {professional.skills && professional.skills.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {professional.skills.slice(0, 4).map((skill, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
                ))}
                {professional.skills.length > 4 && (
                  <Badge variant="secondary" className="text-xs">+{professional.skills.length - 4}</Badge>
                )}
              </div>
            )}

            {showPortfolio && professional.portfolio && professional.portfolio.length > 0 && (
              <div className="mb-4 flex items-center gap-2 text-sm text-accent">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {professional.portfolio.length} Works
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <Badge className={availabilityBadge.className}>{availabilityBadge.text}</Badge>
                {professional.rate && (
                  <p className="text-sm font-semibold text-accent mt-2">{professional.rate}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate(`/profile/${professional.user_id}`)}
                >
                  <Eye className="w-4 h-4 mr-1" />View Profile
                </Button>
                <Button 
                  size="sm" 
                  className="bg-accent hover:bg-accent/90" 
                  onClick={() => onContact(professional)}
                >
                  <MessageSquare className="w-4 h-4 mr-1" />Contact
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfessionalCard;
